import { useState, useEffect, useMemo } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { 
  DollarSign, TrendingUp, TrendingDown, Filter, Calendar, Users,
  ArrowUpRight, ArrowDownRight, Clock, AlertTriangle, RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { Link } from "react-router-dom";

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return "0%";
  return `${value.toFixed(1)}%`;
};

export default function PnLView() {
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [costingData, setCostingData] = useState({});
  const [timeRange, setTimeRange] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [pmFilter, setPmFilter] = useState("all");
  const [pms, setPms] = useState([]);
  
  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [programsRes, usersRes] = await Promise.all([
          apiClient.get("/programs"),
          apiClient.get("/users")
        ]);
        
        setPrograms(programsRes.data || []);
        
        // Filter PMs for dropdown
        const pmUsers = (usersRes.data || []).filter(u => 
          u.role === "COO" || u.role === "Program Manager" || u.role === "Admin"
        );
        setPms(pmUsers);
        
        // Fetch costing data for each program (batch)
        const costingPromises = (programsRes.data || []).map(async (p) => {
          try {
            const res = await apiClient.get(`/costing/program/${p.id}/summary`);
            return { programId: p.id, data: res.data };
          } catch {
            return { programId: p.id, data: null };
          }
        });
        
        const costingResults = await Promise.all(costingPromises);
        const costingMap = {};
        costingResults.forEach(r => {
          if (r.data) costingMap[r.programId] = r.data;
        });
        setCostingData(costingMap);
        
      } catch (err) {
        toast.error("Failed to load P&L data");
      }
      setLoading(false);
    };
    fetchData();
  }, []);
  
  // Apply filters
  const filteredPrograms = useMemo(() => {
    // On the Horizon projects are unsigned — there is no revenue to book against
    // their cost, so they stay out of the P&L entirely. See
    // backend/services/horizon.py for the same rule on the server side.
    let filtered = programs.filter(p => !p.on_horizon);

    // Time range filter (based on created_at)
    if (timeRange !== "all") {
      const now = new Date();
      const cutoff = new Date();
      if (timeRange === "month") cutoff.setMonth(now.getMonth() - 1);
      else if (timeRange === "quarter") cutoff.setMonth(now.getMonth() - 3);
      else if (timeRange === "6months") cutoff.setMonth(now.getMonth() - 6);
      else if (timeRange === "year") cutoff.setFullYear(now.getFullYear() - 1);
      
      filtered = filtered.filter(p => {
        const created = new Date(p.created_at);
        return created >= cutoff;
      });
    }
    
    // PM filter
    if (pmFilter !== "all") {
      filtered = filtered.filter(p => p.project_manager_id === pmFilter);
    }
    
    return filtered;
  }, [programs, timeRange, pmFilter]);
  
  // Calculate aggregates
  const totals = useMemo(() => {
    let contracted = 0;
    let collected = 0;
    let totalCost = 0;
    let laborCost = 0;
    let vendorCost = 0;
    
    filteredPrograms.forEach(p => {
      contracted += p.contract_value || 0;
      collected += p.cash_collected || 0;
      
      const costing = costingData[p.id];
      if (costing) {
        totalCost += costing.total_cost || 0;
        laborCost += costing.total_labor_cost || 0;
        vendorCost += costing.total_vendor_cost || costing.total_non_labor_cost || 0;
      }
    });
    
    const outstanding = contracted - collected;
    const grossProfit = contracted - totalCost;
    const margin = contracted > 0 ? (grossProfit / contracted) * 100 : 0;
    
    return {
      contracted,
      collected,
      outstanding,
      totalCost,
      laborCost,
      vendorCost,
      grossProfit,
      margin
    };
  }, [filteredPrograms, costingData]);
  
  // Category performance aggregates
  const categoryTotals = useMemo(() => {
    const categories = { visualization: { revenue: 0, cost: 0 }, marketing: { revenue: 0, cost: 0 }, vexos: { revenue: 0, cost: 0 }, other: { revenue: 0, cost: 0 } };
    
    filteredPrograms.forEach(p => {
      const costing = costingData[p.id];
      if (costing?.category_performance) {
        costing.category_performance.forEach(cp => {
          if (categories[cp.category]) {
            // Backend returns allocated_revenue and actual_cost
            categories[cp.category].revenue += cp.allocated_revenue || cp.revenue || 0;
            categories[cp.category].cost += cp.actual_cost || cp.cost || 0;
          }
        });
      }
    });
    
    return Object.entries(categories).map(([cat, data]) => ({
      category: cat,
      ...data,
      profit: data.revenue - data.cost,
      margin: data.revenue > 0 ? ((data.revenue - data.cost) / data.revenue) * 100 : 0
    }));
  }, [filteredPrograms, costingData]);
  
  // Most/Least profitable programs
  const profitabilityRanking = useMemo(() => {
    const withProfit = filteredPrograms.map(p => {
      const costing = costingData[p.id];
      // Production margin works to the one-time scope — the subscription term
      // in contract_value isn't what the production cost was spent on.
      const revenue = p.one_time_contract_value ?? p.contract_value ?? 0;
      const profit = costing ? revenue - (costing.total_cost || 0) : revenue;
      const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
      return { ...p, profit, margin };
    }).filter(p => (p.one_time_contract_value ?? p.contract_value) > 0);
    
    const sorted = [...withProfit].sort((a, b) => b.profit - a.profit);
    
    return {
      mostProfitable: sorted.slice(0, 5),
      leastProfitable: sorted.slice(-5).reverse()
    };
  }, [filteredPrograms, costingData]);
  
  // Aging receivables
  const agingReceivables = useMemo(() => {
    const now = new Date();
    return filteredPrograms
      .filter(p => (p.contract_value || 0) > (p.cash_collected || 0))
      .map(p => {
        const outstanding = (p.contract_value || 0) - (p.cash_collected || 0);
        const daysSinceStart = p.start_window ? Math.floor((now - new Date(p.start_window)) / (1000 * 60 * 60 * 24)) : 0;
        return { ...p, outstanding, daysSinceStart };
      })
      .sort((a, b) => b.daysSinceStart - a.daysSinceStart)
      .slice(0, 10);
  }, [filteredPrograms]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="pnl-view">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>Profit & Loss</h1>
          <p className="text-muted-foreground text-sm">Executive overview of financial performance</p>
        </div>
        
        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-36">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Time Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="month">Last Month</SelectItem>
              <SelectItem value="quarter">Last Quarter</SelectItem>
              <SelectItem value="6months">Last 6 Months</SelectItem>
              <SelectItem value="year">Last Year</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={pmFilter} onValueChange={setPmFilter}>
            <SelectTrigger className="w-44">
              <Users className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All PMs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All PMs</SelectItem>
              {pms.map(pm => (
                <SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Contracted</p>
                <p className="text-2xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>{formatCurrency(totals.contracted)}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Collected</p>
                <p className="text-2xl font-light text-green-600" style={{ fontFamily: 'var(--font-sans)' }}>{formatCurrency(totals.collected)}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Outstanding</p>
                <p className="text-2xl font-light text-amber-600" style={{ fontFamily: 'var(--font-sans)' }}>{formatCurrency(totals.outstanding)}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Gross Margin</p>
                <p className={`text-2xl font-light ${totals.margin >= 0 ? 'text-green-600' : 'text-red-600'}`} style={{ fontFamily: 'var(--font-sans)' }}>
                  {formatPercent(totals.margin)}
                </p>
              </div>
              <div className={`w-10 h-10 rounded-full ${totals.margin >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'} flex items-center justify-center`}>
                {totals.margin >= 0 ? (
                  <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* P&L Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">P&L Summary</CardTitle>
            <CardDescription>Revenue vs. Costs breakdown</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm font-medium">Total Revenue (Contracted)</span>
                <span className="font-semibold">{formatCurrency(totals.contracted)}</span>
              </div>
              
              <div className="pl-4 space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Labor Costs</span>
                  <span className="text-red-600">-{formatCurrency(totals.laborCost)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Vendor/External Costs</span>
                  <span className="text-red-600">-{formatCurrency(totals.vendorCost)}</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center py-2 border-t border-b font-medium">
                <span>Total Costs</span>
                <span className="text-red-600">-{formatCurrency(totals.totalCost)}</span>
              </div>
              
              <div className="flex justify-between items-center py-3 bg-muted/50 px-3 rounded-lg">
                <span className="font-semibold">Gross Profit</span>
                <span className={`font-bold text-lg ${totals.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(totals.grossProfit)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Category Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Performance by Category</CardTitle>
            <CardDescription>Revenue and profit by service category</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {categoryTotals.map(cat => (
                <div key={cat.category} className="p-3 border rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium capitalize">{cat.category}</span>
                    <Badge variant={cat.margin >= 30 ? "success" : cat.margin >= 0 ? "secondary" : "destructive"}>
                      {formatPercent(cat.margin)} margin
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Revenue</p>
                      <p className="font-medium">{formatCurrency(cat.revenue)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Cost</p>
                      <p className="font-medium text-red-600">{formatCurrency(cat.cost)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Profit</p>
                      <p className={`font-medium ${cat.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(cat.profit)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Top Drivers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Most Profitable */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              Most Profitable
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {profitabilityRanking.mostProfitable.map((p, i) => (
                <Link
                  key={p.id}
                  to={`/programs/${p.id}`}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-muted-foreground w-4">{i + 1}.</span>
                    <span className="text-sm truncate">{p.program_name}</span>
                  </div>
                  <span className="text-sm font-medium text-green-600 flex-shrink-0">
                    {formatCurrency(p.profit)}
                  </span>
                </Link>
              ))}
              {profitabilityRanking.mostProfitable.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No data</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Least Profitable */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-600" />
              Least Profitable
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {profitabilityRanking.leastProfitable.map((p, i) => (
                <Link
                  key={p.id}
                  to={`/programs/${p.id}`}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-muted-foreground w-4">{i + 1}.</span>
                    <span className="text-sm truncate">{p.program_name}</span>
                  </div>
                  <span className={`text-sm font-medium flex-shrink-0 ${p.profit >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                    {formatCurrency(p.profit)}
                  </span>
                </Link>
              ))}
              {profitabilityRanking.leastProfitable.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No data</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Aging Receivables */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Aging Receivables
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {agingReceivables.map((p) => (
                <Link
                  key={p.id}
                  to={`/programs/${p.id}`}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm truncate">{p.program_name}</p>
                    <p className="text-xs text-muted-foreground">{p.daysSinceStart} days</p>
                  </div>
                  <span className="text-sm font-medium text-amber-600 flex-shrink-0">
                    {formatCurrency(p.outstanding)}
                  </span>
                </Link>
              ))}
              {agingReceivables.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No outstanding receivables</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Collection Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Collection Progress</CardTitle>
          <CardDescription>Overall payment collection status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {formatCurrency(totals.collected)} collected of {formatCurrency(totals.contracted)}
              </span>
              <span className="font-medium">
                {totals.contracted > 0 ? formatPercent((totals.collected / totals.contracted) * 100) : "0%"}
              </span>
            </div>
            <Progress 
              value={totals.contracted > 0 ? (totals.collected / totals.contracted) * 100 : 0} 
              className="h-3"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
